<template>
  <BaseDialogForm
    v-model="showCustomerLocationUpdateForm"
    title="Update Location Info"
    maxWidth="80vw"
    :nonFieldErrors="formErrors.non_field_errors"
    :persistent="true"
  >
    <template #content>
      <v-row no-gutters>
        <v-col cols="12">
          <v-alert type="info" class="white--text" dense>
            <span>You can drag the marker to update the coordinates!</span>
          </v-alert>
        </v-col>
        <v-col cols="12" md="6" class="pr-2 border-right">
          <v-form
            v-model="isValid"
            ref="customerLocationUpdateForm"
            autocomplete="off"
          >
            <v-row no-gutters class="w-100">
              <v-col cols="12" md="6" class="px-2 py-4">
                <InputField
                  type="number"
                  label="Contact Number"
                  class="background-white"
                  name="contact_number"
                  :rules="[
                    (val) =>
                      !val ||
                      (val.length >= 9 && val.length <= 12) ||
                      'Contact number should be between 9 to 12 digits!',
                  ]"
                  :error-messages="formErrors.contact_number"
                  v-model="customerLocationInfo.contact_number"
                  @input="delete formErrors.contact_number"
                />
              </v-col>
              <v-col cols="12" md="6" class="px-2 py-4">
                <InputField
                  label="Contact Person"
                  class="background-white"
                  name="contact_person"
                  :error-messages="formErrors.contact_person"
                  v-model="customerLocationInfo.contact_person"
                  @input="delete formErrors.contact_person"
                />
              </v-col>
              <v-col cols="12" class="px-2 py-4">
                <InputField
                  type="text"
                  name="address_info"
                  label="Building / Apartment"
                  v-model="customerLocationInfo.address_info"
                />
              </v-col>
              <v-col cols="12" class="px-2 py-4">
                <v-text-field
                  ref="customerAddress"
                  outlined
                  id="customerAddress"
                  name="address"
                  placeholder="Address*"
                  hide-details="auto"
                  dense
                  class="background-white"
                  :rules="[(v) => !!v || `Address is required`]"
                  :error-messages="formErrors.address"
                  v-model.trim="customerLocationInfo.address"
                  @input="
                    delete formErrors.address;
                    delete formErrors.coordinates;
                  "
                >
                  <template #append>
                    <v-icon @click="getCurrentLocation"
                      >mdi-crosshairs-gps</v-icon
                    >
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="px-2 py-4">
                <InputField
                  type="text"
                  name="latitude"
                  label="Latitude"
                  class="unassigned lighten-3"
                  :disabled="true"
                  :required="true"
                  :error-messages="formErrors.coordinates"
                  v-model.number="customerLocationInfo.coordinates.latitude"
                  @input="delete formErrors.coordinates"
                />
              </v-col>
              <v-col cols="12" md="6" class="px-2 py-4">
                <InputField
                  type="text"
                  name="longitude"
                  label="Longitude"
                  class="unassigned lighten-3"
                  :disabled="true"
                  :required="true"
                  v-model.number="customerLocationInfo.coordinates.longitude"
                  :error-messages="formErrors.coordinates"
                  @input="delete formErrors.coordinates"
                />
              </v-col>
              <v-col cols="6" md="6" class="px-2 py-4">
                <InputField
                  v-model="customerLocationInfo.from_time"
                  label="From Time"
                  append-icon=""
                  name="from_time"
                  type="time"
                  :error-messages="formErrors.from_time"
                  :required="true"
                  @input="delete formErrors.from_time"
                />
              </v-col>
              <v-col cols="6" md="6" class="px-2 py-4">
                <InputField
                  v-model="customerLocationInfo.to_time"
                  label="To Time"
                  name="to_time"
                  append-icon=""
                  type="time"
                  :error-messages="formErrors.to_time"
                  :required="true"
                  @input="delete formErrors.to_time"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-col>

        <v-col cols="12" md="6" class="pt-4 pl-2">
          <GoogleMap
            mapId="customerLocationUpdateMap"
            mapWidth="100%"
            mapHeight="calc(100vh - 535px)"
            ref="customerLocationUpdateMap"
            :isDrawable="false"
            :showLocationName="false"
            :hasMapInfo="false"
            @locationUpdated="locationUpdated"
          />
        </v-col>
      </v-row>
    </template>
    <template #actions>
      <PrimaryButton
        :disabled="!isValid"
        btnClass="text-uppercase rounded-lg"
        name="Submit"
        @click="updateCustomerLocationInfo"
      />
    </template>
  </BaseDialogForm>
</template>

<script>
import GoogleMap from "@/components/BaseComponents/GoogleMap.vue";
import BaseDialogForm from "@/components/LayoutComponents/BaseDialogForm.vue";
import { bus } from "@/main";
import { loadGoogleInstance } from "@/utils/functions";

export default {
  emits: ["refreshData"],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [String, Number],
    },
  },
  data() {
    return {
      customerLocationInfo: {
        coordinates: {},
      },
      formErrors: {},
      isValid: false,
    };
  },
  computed: {
    showCustomerLocationUpdateForm: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.getCustomerLocationInfo(this.id);
        this.setAutoComplete();
      }
    },
  },
  methods: {
    getCurrentLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.loadLocationMarker({
            coordinates: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
          });
        });
      } else {
        bus.$emit("showToastMessage", {
          message: "Geo location is not supported on the this device!",
          color: "error",
        });
      }
    },
    locationUpdated(evt) {
      let lat = evt.latLng.lat().toFixed(6);
      let lon = evt.latLng.lng().toFixed(6);

      this.customerLocationInfo = {
        ...this.customerLocationInfo,
        coordinates: {
          latitude: parseFloat(lat),
          longitude: parseFloat(lon),
        },
      };
    },
    loadLocationMarker(data) {
      let inter = setInterval(() => {
        if (this.$refs.customerLocationUpdateMap && window.google) {
          clearInterval(inter);
          this.$refs.customerLocationUpdateMap.removeAllMarkers();
          this.$refs.customerLocationUpdateMap.addMarker(data, "other", true);
        }
      }, 100);
    },
    getCustomerLocationInfo(id) {
      bus.$emit("showLoader", true);
      this.$api.orderTracking
        .getDeliveryLocationForOrder(id)
        .then((res) => {
          bus.$emit("showLoader", false);
          this.customerLocationInfo = res.data;
          this.loadLocationMarker(res.data);
        })
        .catch((err) => {
          bus.$emit("showLoader", false);
          console.log(err);
        });
    },
    updateCustomerLocationInfo() {
      bus.$emit("showLoader", true);
      this.$api.orderTracking
        .updateDeliveryLocationForOrder(this.id, {
          ...this.customerLocationInfo,
        })
        .then((res) => {
          bus.$emit("showLoader", false);
          bus.$emit("showToastMessage", {
            message: "Information updated successfully!",
            color: "success",
          });
          this.$emit("refreshData");
          this.showCustomerLocationUpdateForm = false;
        })
        .catch((err) => {
          bus.$emit("showLoader", false);
          bus.$emit("showToastMessage", {
            message: "Couldn't updated information!",
            color: "error",
          });
          console.log(err);
        });
    },
    setAutoComplete() {
      const interval = setInterval(async () => {
        if (this.$refs.customerAddress && window.google && window.google.maps) {
          clearInterval(interval);
          let input = document.getElementById("customerAddress");

          this.autocomplete = new window.google.maps.places.Autocomplete(input);

          if (this.customerLocationInfo.country_code) {
            this.autocomplete.setComponentRestrictions({
              country: [this.customerLocationInfo.country_code],
            });
          }

          this.autocomplete.addListener("place_changed", () => {
            let place = this.autocomplete.getPlace();
            let lat = place.geometry.location.lat().toFixed(6);
            let lon = place.geometry.location.lng().toFixed(6);

            this.customerLocationInfo = {
              ...this.customerLocationInfo,
              address: place.name + ", " + place.formatted_address,
              coordinates: {
                latitude: parseFloat(lat),
                longitude: parseFloat(lon),
              },
            };
            this.loadLocationMarker(this.customerLocationInfo);
          });
        } else {
          loadGoogleInstance();
        }
      }, 100);
    },
  },
  components: { BaseDialogForm, GoogleMap },
};
</script>

<style></style>
